import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { authApi } from '../../api/auth';
import { ApiError } from '../../api/client';
import { useAuthStore, useHasAccount } from '../../state/authStore';

/**
 * Straight into a filled account: no email, no password, nothing to abandon halfway.
 *
 * For traffic arriving from an ad, the distance between the click and seeing a real figure is the
 * whole conversion, and a signup form sits in the middle of it. The account is a throwaway — its
 * own rows, swept up after a day — so a visitor can delete an invoice or change the insurance base
 * without spoiling it for the next person, which a single shared demo login cannot promise.
 */
export function DemoButton({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const hasAccount = useHasAccount();

  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function start() {
    setBusy(true);
    setFailed(false);

    try {
      await authApi.startDemo(i18n.resolvedLanguage === 'en' ? 'en' : 'bg');
      setUser(await authApi.me());
      void navigate('/dashboard');
    } catch (e) {
      // The server refuses to start a demo over a real session (409): the account is right there.
      if (e instanceof ApiError && e.status === 409) {
        void navigate('/dashboard');
        return;
      }

      // A demo that cannot start is not worth an error page; the ordinary signup still works.
      setFailed(true);
      setBusy(false);
    }
  }

  // Starting a demo signs this browser into a sample person's account. Offered to somebody who is
  // signed into their own, it would replace their figures with invented ones at a single click.
  if (hasAccount) {
    return null;
  }

  return (
    <>
      <button type="button" className={className} disabled={busy} onClick={() => void start()}>
        {busy ? t('marketing.demoStarting') : t('marketing.demoCta')}
      </button>
      {failed && <p className="l-demo-failed">{t('common.error')}</p>}
    </>
  );
}
